import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Upload, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { api } from '@/lib/api';
import { AAWTA } from '@/lib/aawta';
import Navigation from '@/components/site/Navigation';
import Footer from '@/components/site/Footer';

const PARTICIPATION_TYPES = [
  'Sponsor',
  'Panelist',
  'Moderator',
  'Exhibitor',
  'Breakout Session Leader',
  'Roundtable Facilitator',
];

const SPONSORSHIP_LEVEL_OPTIONS = [
  'Presenting Partner ($20,000)',
  'Workforce Champion ($10,000)',
  'Innovation Partner ($5,000)',
  'Education Partner ($2,500)',
  'Community Partner ($1,000)',
  'Not sure — please advise',
];

const Section = ({ title, kicker, children }) => (
  <Card className="p-6 md:p-8 rounded-2xl border border-aawta-border shadow-aawta-soft space-y-5" data-testid={`section-${(kicker||title).toLowerCase().replace(/\s+/g,'-')}`}>
    <div>
      {kicker && <div className="overline text-aawta-magenta">{kicker}</div>}
      <h3 className="font-display font-semibold text-xl text-aawta-navy mt-1">{title}</h3>
    </div>
    <div className="space-y-4">{children}</div>
  </Card>
);

const Field = ({ label, children, required, hint, testId }) => (
  <div className="space-y-1.5" data-testid={testId ? `field-${testId}` : undefined}>
    <Label className="text-[13.5px] font-medium text-aawta-navy">
      {label} {required && <span className="text-aawta-magenta">*</span>}
    </Label>
    {children}
    {hint && <div className="text-[12px] text-aawta-muted">{hint}</div>}
  </div>
);

const FileInput = ({ id, onChange, accept, label, testId }) => (
  <label htmlFor={id} data-testid={testId} className="flex items-center gap-3 cursor-pointer rounded-xl border border-dashed border-aawta-border bg-aawta-surface px-4 py-3 hover:border-aawta-magenta/50 transition-colors">
    <Upload className="h-5 w-5 text-aawta-magenta shrink-0" />
    <span className="text-[14px] text-aawta-navy font-medium truncate">{label}</span>
    <input id={id} type="file" accept={accept} className="hidden" onChange={onChange} />
  </label>
);

const Participate = () => {
  const navigate = useNavigate();
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [form, setForm] = useState({});
  const [files, setFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggleType = (t) =>
    setSelectedTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: typeof e === 'string' || typeof e === 'boolean' ? e : e.target.value }));

  const onFile = (key) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFiles((prev) => ({ ...prev, [key]: f }));
  };

  const showSponsor = selectedTypes.includes('Sponsor');
  const showPanelist = selectedTypes.includes('Panelist');
  const showModerator = selectedTypes.includes('Moderator');
  const showExhibitor = selectedTypes.includes('Exhibitor');
  const showBreakout = selectedTypes.includes('Breakout Session Leader');
  const showRoundtable = selectedTypes.includes('Roundtable Facilitator');

  const submit = async (e) => {
    e.preventDefault();
    if (selectedTypes.length === 0) {
      toast.error('Please select at least one participation type.');
      return;
    }
    if (!form.organization_name || !form.primary_contact_name || !form.email) {
      toast.error('Organization, contact name, and email are required.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('payload', JSON.stringify({
        ...form,
        participation_types: selectedTypes,
        marketing_permission: !!form.marketing_permission,
      }));
      Object.entries(files).forEach(([field, file]) => {
        if (file) fd.append(field, file);
      });
      await api.post('/submissions', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Submission failed. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <main className="pt-32 pb-24">
          <div className="max-w-2xl mx-auto px-6 lg:px-10 text-center reveal" data-testid="participate-thank-you">
            <div className="w-16 h-16 rounded-full bg-aawta-magenta/10 text-aawta-magenta mx-auto flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8" strokeWidth={2} />
            </div>
            <h1 className="font-display font-bold text-4xl md:text-5xl mt-6 text-aawta-navy tracking-tight leading-[1.1]">
              Thank You!
            </h1>
            <div className="divider-magenta mt-6 mx-auto" />
            <p className="mt-6 text-[17px] leading-relaxed text-aawta-muted">
              Your participation information has been successfully submitted. The AAWTA team
              will review your submission and contact you with any additional event details or
              next steps.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Button
                onClick={() => navigate('/')}
                data-testid="thank-you-home"
                className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white px-7 py-6"
              >
                Back to Home
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open(AAWTA.eventbriteUrl, '_blank', 'noopener,noreferrer')}
                className="rounded-full border-aawta-navy text-aawta-navy hover:bg-aawta-navy hover:text-white px-7 py-6"
              >
                Register for the Summit
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aawta-surface">
      <Navigation />
      <main className="pt-28 pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <button onClick={() => navigate('/')} data-testid="participate-back" className="inline-flex items-center gap-2 text-aawta-muted hover:text-aawta-navy text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </button>

          <div className="mt-6">
            <span className="overline text-aawta-magenta">Future Workforce Summit 2026</span>
            <h1 className="font-display font-bold text-4xl md:text-5xl mt-3 text-aawta-navy tracking-tight leading-[1.05]">
              Speaker, Sponsor &amp; Exhibitor Participation Form
            </h1>
            <div className="divider-magenta mt-6" />
            <p className="mt-6 text-[16px] leading-relaxed text-aawta-muted max-w-3xl">
              Thank you for participating in the Future Workforce Summit 2026, hosted by the
              African American Women Trucking Association (AAWTA). This form lets our
              sponsors, speakers, exhibitors, moderators, breakout leaders, and roundtable
              facilitators submit the information needed for event planning, marketing, the
              official Summit program, website listings, sponsor recognition, and exhibitor
              promotion. Please complete only the sections that apply to your role.
            </p>
          </div>

          <form onSubmit={submit} className="mt-10 space-y-6">
            <Section kicker="Step 1" title="How are you participating? (select all that apply)">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PARTICIPATION_TYPES.map((t) => {
                  const active = selectedTypes.includes(t);
                  return (
                    <label
                      key={t}
                      data-testid={`participation-type-${t.toLowerCase().replace(/\s+/g,'-')}`}
                      className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-all duration-200 ${
                        active ? 'bg-aawta-magenta/10 border-aawta-magenta text-aawta-navy' : 'bg-white border-aawta-border hover:border-aawta-magenta/40'
                      }`}
                    >
                      <Checkbox checked={active} onCheckedChange={() => toggleType(t)} className="border-aawta-navy data-[state=checked]:bg-aawta-magenta data-[state=checked]:border-aawta-magenta" />
                      <span className="font-medium text-[14.5px]">{t}</span>
                    </label>
                  );
                })}
              </div>
            </Section>

            <Section kicker="Required" title="Required Information (for every participant)">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Organization name" required>
                  <Input required value={form.organization_name || ''} onChange={set('organization_name')} className="rounded-lg" data-testid="input-organization-name" />
                </Field>
                <Field label="Primary contact name" required>
                  <Input required value={form.primary_contact_name || ''} onChange={set('primary_contact_name')} className="rounded-lg" data-testid="input-contact-name" />
                </Field>
                <Field label="Job title">
                  <Input value={form.job_title || ''} onChange={set('job_title')} className="rounded-lg" data-testid="input-job-title" />
                </Field>
                <Field label="Email address" required>
                  <Input type="email" required value={form.email || ''} onChange={set('email')} className="rounded-lg" data-testid="input-email" />
                </Field>
                <Field label="Phone number">
                  <Input value={form.phone || ''} onChange={set('phone')} className="rounded-lg" data-testid="input-phone" />
                </Field>
                <Field label="Company website">
                  <Input value={form.company_website || ''} onChange={set('company_website')} placeholder="https://" className="rounded-lg" data-testid="input-website" />
                </Field>
              </div>
              <Field label="Organization description">
                <Textarea rows={3} value={form.organization_description || ''} onChange={set('organization_description')} className="rounded-lg resize-none" data-testid="input-description" />
              </Field>
              <Field label="Logo upload (PNG, JPG — up to 10MB)">
                <FileInput id="logo" accept="image/*" onChange={onFile('logo')} testId="input-logo"
                  label={files.logo?.name || 'Upload your organization logo'} />
              </Field>
              <Field label="Additional notes">
                <Textarea rows={2} value={form.additional_notes || ''} onChange={set('additional_notes')} className="rounded-lg resize-none" data-testid="input-notes" />
              </Field>
              <label className="flex items-start gap-3 mt-1">
                <Checkbox
                  checked={!!form.marketing_permission}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, marketing_permission: !!v }))}
                  className="mt-0.5 border-aawta-navy data-[state=checked]:bg-aawta-magenta data-[state=checked]:border-aawta-magenta"
                  data-testid="input-marketing-permission"
                />
                <span className="text-[13.5px] text-aawta-muted leading-snug">
                  I grant AAWTA permission to feature our logo, description, and submitted media
                  across Summit marketing (website, program, social) for promotional purposes.
                </span>
              </label>
            </Section>

            {showSponsor && (
              <Section kicker="Sponsor" title="Sponsor Details">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Sponsorship level of interest">
                    <select
                      value={form.sponsorship_level || ''}
                      onChange={set('sponsorship_level')}
                      className="w-full rounded-lg border border-aawta-border bg-white h-10 px-3 text-sm"
                      data-testid="input-sponsorship-level"
                    >
                      <option value="">Select level…</option>
                      {SPONSORSHIP_LEVEL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Social media links (comma separated)">
                    <Input value={form.social_media || ''} onChange={set('social_media')} className="rounded-lg" placeholder="LinkedIn, Instagram, Facebook…" />
                  </Field>
                </div>
                <Field label="Company description (for program / website)">
                  <Textarea rows={3} value={form.company_description || ''} onChange={set('company_description')} className="rounded-lg resize-none" />
                </Field>
                <Field label="Sponsor Showcase information (story, video link, CTA, etc.)">
                  <Textarea rows={3} value={form.sponsor_showcase_info || ''} onChange={set('sponsor_showcase_info')} className="rounded-lg resize-none" />
                </Field>
                <Field label="Featured banner image (optional)">
                  <FileInput id="banner" accept="image/*" onChange={onFile('banner')} label={files.banner?.name || 'Upload banner'} />
                </Field>
                <Field label="Promotional video (optional — up to 200MB)">
                  <FileInput id="video" accept="video/*" onChange={onFile('video')} label={files.video?.name || 'Upload video'} />
                </Field>
              </Section>
            )}

            {(showPanelist || showModerator || showBreakout || showRoundtable) && (
              <Section kicker="Speaker" title="Speaker / Presenter Details">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Speaker name (as it should appear)">
                    <Input value={form.speaker_name || ''} onChange={set('speaker_name')} className="rounded-lg" />
                  </Field>
                  <Field label="LinkedIn profile">
                    <Input value={form.linkedin || ''} onChange={set('linkedin')} placeholder="https://linkedin.com/in/…" className="rounded-lg" />
                  </Field>
                </div>
                <Field label="Professional biography">
                  <Textarea rows={4} value={form.speaker_bio || ''} onChange={set('speaker_bio')} className="rounded-lg resize-none" />
                </Field>
                <Field label="Headshot upload">
                  <FileInput id="headshot" accept="image/*" onChange={onFile('headshot')} label={files.headshot?.name || 'Upload headshot'} />
                </Field>
                <Field label="Accessibility or special requirements">
                  <Textarea rows={2} value={form.accessibility_requirements || ''} onChange={set('accessibility_requirements')} className="rounded-lg resize-none" />
                </Field>
              </Section>
            )}

            {(showPanelist || showModerator) && (
              <Section kicker="Panel" title="Panel Assignment & Topics">
                <Field label="Preferred panel">
                  <Input value={form.preferred_panel || ''} onChange={set('preferred_panel')} className="rounded-lg" placeholder="Education & Industry Panel / Workforce Development Panel" />
                </Field>
                <Field label="Discussion topics you can speak to">
                  <Textarea rows={3} value={form.discussion_topics || ''} onChange={set('discussion_topics')} className="rounded-lg resize-none" />
                </Field>
                {showModerator && (
                  <>
                    <Field label="Moderating experience">
                      <Textarea rows={2} value={form.moderating_experience || ''} onChange={set('moderating_experience')} className="rounded-lg resize-none" />
                    </Field>
                    <Field label="Suggested discussion questions">
                      <Textarea rows={3} value={form.suggested_questions || ''} onChange={set('suggested_questions')} className="rounded-lg resize-none" />
                    </Field>
                  </>
                )}
              </Section>
            )}

            {showExhibitor && (
              <Section kicker="Exhibitor" title="Exhibitor Details">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Booth requirements (size, layout, table/booth)">
                    <Input value={form.booth_requirements || ''} onChange={set('booth_requirements')} className="rounded-lg" />
                  </Field>
                  <Field label="Power requirements">
                    <Input value={form.power_requirements || ''} onChange={set('power_requirements')} className="rounded-lg" placeholder="e.g. 1 outlet, 20 amp" />
                  </Field>
                  <Field label="Internet requirements">
                    <Input value={form.internet_requirements || ''} onChange={set('internet_requirements')} className="rounded-lg" />
                  </Field>
                  <Field label="Equipment needs">
                    <Input value={form.equipment_needs || ''} onChange={set('equipment_needs')} className="rounded-lg" placeholder="e.g. monitor, chairs, easel" />
                  </Field>
                </div>
                <Field label="Product demonstration information">
                  <Textarea rows={2} value={form.demo_information || ''} onChange={set('demo_information')} className="rounded-lg resize-none" />
                </Field>
                <Field label="Giveaway information">
                  <Textarea rows={2} value={form.giveaway_information || ''} onChange={set('giveaway_information')} className="rounded-lg resize-none" />
                </Field>
                <Field label="Exhibitor directory listing (short description for the public directory)">
                  <Textarea rows={3} value={form.directory_listing || ''} onChange={set('directory_listing')} className="rounded-lg resize-none" />
                </Field>
                <Field label="Brochure or product one-pager (optional PDF — up to 25MB)">
                  <FileInput id="brochure" accept="application/pdf" onChange={onFile('brochure')} label={files.brochure?.name || 'Upload brochure'} />
                </Field>
              </Section>
            )}

            {showBreakout && (
              <Section kicker="Breakout" title="Breakout Session Details">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Preferred breakout track">
                    <Input value={form.breakout_track || ''} onChange={set('breakout_track')} className="rounded-lg" />
                  </Field>
                  <Field label="Session title">
                    <Input value={form.session_title || ''} onChange={set('session_title')} className="rounded-lg" />
                  </Field>
                </div>
                <Field label="Session description">
                  <Textarea rows={3} value={form.session_description || ''} onChange={set('session_description')} className="rounded-lg resize-none" />
                </Field>
                <Field label="Learning objectives">
                  <Textarea rows={3} value={form.learning_objectives || ''} onChange={set('learning_objectives')} className="rounded-lg resize-none" />
                </Field>
                <Field label="Technology requirements">
                  <Input value={form.technology_requirements || ''} onChange={set('technology_requirements')} className="rounded-lg" placeholder="AV, projector, mics, etc." />
                </Field>
              </Section>
            )}

            {showRoundtable && (
              <Section kicker="Roundtable" title="Roundtable Facilitation Details">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Preferred roundtable topic">
                    <Input value={form.roundtable_topic || ''} onChange={set('roundtable_topic')} className="rounded-lg" />
                  </Field>
                  <Field label="Alternate topic">
                    <Input value={form.alternate_topic || ''} onChange={set('alternate_topic')} className="rounded-lg" />
                  </Field>
                </div>
                <Field label="Discussion objective">
                  <Textarea rows={2} value={form.discussion_objective || ''} onChange={set('discussion_objective')} className="rounded-lg resize-none" />
                </Field>
                <Field label="Key questions">
                  <Textarea rows={3} value={form.key_questions || ''} onChange={set('key_questions')} className="rounded-lg resize-none" />
                </Field>
                <Field label="Desired participant outcomes">
                  <Textarea rows={2} value={form.desired_outcomes || ''} onChange={set('desired_outcomes')} className="rounded-lg resize-none" />
                </Field>
              </Section>
            )}

            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <div className="text-[13px] text-aawta-muted">
                Only the sections you selected above will be submitted.
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="participate-submit"
                className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white px-8 py-6 font-medium shadow-aawta-magenta"
              >
                {submitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</>
                ) : (
                  <>Submit Participation Form <ArrowRight className="ml-2 h-4 w-4" /></>
                )}
              </Button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Participate;
